import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { EFEITO_LABELS, NATUREZA_LABELS } from '@/lib/deadlines'
import { formatDateBr } from '@/lib/publication-inbox'
import type { CalendarDay } from '@/services/deadline-service'

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

function weekday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
}

function scope(day: CalendarDay): string {
  if (day.abrangencia === 'NACIONAL') return 'Nacional'
  if (day.abrangencia === 'ESTADUAL') return `Estadual · ${day.uf}`
  return [day.tribunal, day.uf].filter(Boolean).join(' · ')
}

interface Props {
  days: CalendarDay[]
  canEdit: boolean
  onToggle: (day: CalendarDay, patch: { ativo?: boolean; aConferir?: boolean }) => void
}

/** Dias sem prazo com a fonte de cada um e o selo "a conferir". */
export function CalendarDaysTable({ days, canEdit, onToggle }: Props) {
  if (!days.length) return <p className="text-sm text-muted-foreground">Nenhum dia cadastrado para o filtro.</p>
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Data</TableHead>
          <TableHead>Descrição</TableHead>
          <TableHead>Âmbito</TableHead>
          <TableHead>Efeito</TableHead>
          <TableHead>Fonte</TableHead>
          {canEdit && <TableHead className="text-right">Ações</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {days.map((day) => (
          <TableRow key={day.id} className={day.ativo ? '' : 'opacity-50'}>
            <TableCell className="whitespace-nowrap tabular-nums">{formatDateBr(day.data)} <span className="text-muted-foreground">{weekday(day.data)}</span></TableCell>
            <TableCell className="max-w-md whitespace-normal">
              {day.descricao}
              <div className="mt-0.5 flex flex-wrap gap-1">
                <Badge variant="outline">{NATUREZA_LABELS[day.natureza]}</Badge>
                {day.aConferir && <Badge variant="outline" className="border-destructive/50 text-destructive">a conferir</Badge>}
                {!day.ativo && <Badge variant="secondary">desativado</Badge>}
              </div>
            </TableCell>
            <TableCell className="whitespace-nowrap">{scope(day)}</TableCell>
            <TableCell className="whitespace-nowrap">{EFEITO_LABELS[day.efeito]}</TableCell>
            <TableCell className="max-w-xs whitespace-normal text-xs">
              {day.fonteUrl ? <a href={day.fonteUrl} target="_blank" rel="noreferrer noopener" className="underline">abrir fonte</a> : <span className="text-muted-foreground">sem fonte</span>}
              {day.trecho && <p className="mt-0.5 italic text-muted-foreground">“{day.trecho}”</p>}
            </TableCell>
            {canEdit && (
              <TableCell className="whitespace-nowrap text-right">
                <Button size="xs" variant="ghost" onClick={() => onToggle(day, { aConferir: !day.aConferir })}>
                  {day.aConferir ? 'Conferido' : 'Marcar a conferir'}
                </Button>
                <Button size="xs" variant="ghost" onClick={() => onToggle(day, { ativo: !day.ativo })}>
                  {day.ativo ? 'Desativar' : 'Reativar'}
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
